import React from 'react'

import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/20/solid'

import { Application, ApplicationCalculationsType } from '@/graphql/generated'

export const checkWithinThreshold = (
  eventDate: string,
  creditReportDate: string,
  threshold: number,
  upperThreshold?: number
) => {
  if (!eventDate || !creditReportDate) return ''

  const normalizeDate = (date: string) => {
    const d = new Date(date)
    return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).getTime()
  }

  const eventDateTime = normalizeDate(eventDate)
  const creditReportDateTime = normalizeDate(creditReportDate)
  const millisecondsInYear = 365.25 * 24 * 60 * 60 * 1000
  const timeDifference = creditReportDateTime - eventDateTime

  if (upperThreshold && upperThreshold > threshold) {
    const lowerYearsMs = threshold * millisecondsInYear
    const upperYearsMs = upperThreshold * millisecondsInYear

    if (timeDifference <= upperYearsMs && timeDifference > lowerYearsMs) {
      return `>= ${threshold} and <= ${upperThreshold} years`
    }

    if (timeDifference < lowerYearsMs) {
      return `< ${threshold} years`
    }
  } else {
    if (threshold && timeDifference <= threshold * millisecondsInYear) {
      return `< ${threshold} years`
    }
  }

  return ''
}

type CreditSummaryMatrixProps = {
  calcs: ApplicationCalculationsType
  creditReport: any
  application: Application
}

const CreditSummaryMatrix = ({ calcs, creditReport, application }: CreditSummaryMatrixProps) => {
  const creditReportDate = creditReport ? creditReport.createdAt : null

  if (creditReportDate === null) return null

  const lastBankruptcyWithin5Years = checkWithinThreshold(
    calcs?.lastBankruptcy,
    creditReportDate,
    3,
    5
  )
  const lastBankruptcyWithin3Years = checkWithinThreshold(
    calcs?.lastBankruptcy,
    creditReportDate,
    3
  )

  const lastForeclosureWithin5Years = checkWithinThreshold(
    calcs?.lastForeclosure,
    creditReportDate,
    3,
    5
  )
  const lastForeclosureWithin3Years = checkWithinThreshold(
    calcs?.lastForeclosure,
    creditReportDate,
    3
  )
  const last90DeliquencyWithin2Years = checkWithinThreshold(
    calcs?.last90Deliquency,
    creditReportDate,
    2
  )
  const last120DeliquencyWithin2Years = checkWithinThreshold(
    calcs?.last120Deliquency,
    creditReportDate,
    2
  )

  const lastDilWithin5Years = checkWithinThreshold(calcs?.lastDeedInLieu, creditReportDate, 3, 5)
  const lastDilWithin3Years = checkWithinThreshold(calcs?.lastDeedInLieu, creditReportDate, 3)

  const lastShortSaleWithin5Years = checkWithinThreshold(
    calcs?.lastShortSale,
    creditReportDate,
    3,
    5
  )
  const lastShortSaleWithin3Years = checkWithinThreshold(calcs?.lastShortSale, creditReportDate, 3)

  const getCreditEventIcon = (within3Years: string, within5Years: string) => {
    if (within3Years && application.pricingVersion >= 22) {
      return (
        <ExclamationCircleIcon
          width={20}
          className='text-error'
        />
      )
    }
    if (within5Years) {
      return (
        <ExclamationCircleIcon
          width={20}
          className='text-warning'
        />
      )
    }
    return (
      <CheckCircleIcon
        width={20}
        className='text-success'
      />
    )
  }

  return (
    <table className='table table-sm w-full text-left text-base'>
      <thead>
        <tr>
          <th scope='col'>Type</th>
          <th scope='col'>Status Date</th>
          <th scope='col'>Validation</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>BK</td>
          <td>
            {lastBankruptcyWithin5Years && (
              <>
                <div> {calcs?.lastBankruptcy}</div>
                <div>{lastBankruptcyWithin3Years || lastBankruptcyWithin5Years}</div>
              </>
            )}
          </td>
          <td>
            <div className='flex justify-center items-center'>
              {getCreditEventIcon(lastBankruptcyWithin3Years, lastBankruptcyWithin5Years)}
            </div>
          </td>
        </tr>
        {application.pricingVersion >= 22 && (
          <>
            <tr>
              <td>DIL</td>
              <td>
                {lastDilWithin5Years && (
                  <>
                    <div> {calcs?.lastDeedInLieu}</div>
                    <div>{lastDilWithin3Years || lastDilWithin5Years}</div>
                  </>
                )}
              </td>
              <td>
                <div className='flex justify-center items-center'>
                  {getCreditEventIcon(lastDilWithin3Years, lastDilWithin5Years)}
                </div>
              </td>
            </tr>
            <tr>
              <td>FC</td>
              <td>
                {lastForeclosureWithin5Years && (
                  <>
                    <div> {calcs?.lastForeclosure}</div>
                    <div>{lastForeclosureWithin3Years || lastForeclosureWithin5Years}</div>
                  </>
                )}
              </td>
              <td>
                <div className='flex justify-center items-center'>
                  {getCreditEventIcon(lastForeclosureWithin3Years, lastForeclosureWithin5Years)}
                </div>
              </td>
            </tr>
          </>
        )}
        <tr>
          <td>MTG DQ 90</td>
          <td>
            {last90DeliquencyWithin2Years && (
              <>
                <div> {calcs?.last90Deliquency}</div>
                <div>{last90DeliquencyWithin2Years}</div>
              </>
            )}
          </td>
          <td>
            <div className='flex justify-center items-center'>
              {last90DeliquencyWithin2Years ? (
                <ExclamationCircleIcon
                  width={20}
                  className='text-error'
                />
              ) : (
                <CheckCircleIcon
                  width={20}
                  className='text-success'
                />
              )}
            </div>
          </td>
        </tr>
        <tr>
          <td>MTG DQ 120+</td>
          <td>
            {last120DeliquencyWithin2Years && (
              <>
                <div> {calcs?.last120Deliquency}</div>
                <div>{last120DeliquencyWithin2Years}</div>
              </>
            )}
          </td>
          <td>
            <div className='flex justify-center items-center'>
              {last120DeliquencyWithin2Years ? (
                <ExclamationCircleIcon
                  width={20}
                  className='text-error'
                />
              ) : (
                <CheckCircleIcon
                  width={20}
                  className='text-success'
                />
              )}
            </div>
          </td>
        </tr>
        {application.pricingVersion >= 22 && (
          <tr>
            <td>SS</td>
            <td>
              {lastShortSaleWithin5Years && (
                <>
                  <div> {calcs?.lastShortSale}</div>
                  <div>{lastShortSaleWithin3Years || lastShortSaleWithin5Years}</div>
                </>
              )}
            </td>
            <td>
              <div className='flex justify-center items-center'>
                {getCreditEventIcon(lastShortSaleWithin3Years, lastShortSaleWithin5Years)}
              </div>
            </td>
          </tr>
        )}
      </tbody>
    </table>
  )
}

export default CreditSummaryMatrix
